import { supabase } from './supabaseClient';
import { systemLogger } from './logging';

interface RemoteErrorLog {
  errorType: string;
  errorMessage: string;
  errorStack?: string | undefined;
  operationName: string;
  operationPayload?: unknown;
  sessionValid?: boolean;
  jwtExpiresAt?: string;
  browserInfo?: {
    userAgent: string;
    language: string;
    online: boolean;
    cookiesEnabled: boolean;
  } | undefined;
}

/**
 * Log error to remote database for production debugging
 * This runs silently - user sees nothing except error ID in error message
 *
 * Usage:
 * const errorId = await logErrorRemotely({
 *   errorType: 'timeout',
 *   errorMessage: 'Operation timed out',
 *   operationName: 'create-comment',
 *   operationPayload: { entityId: '123' }
 * });
 */
export async function logErrorRemotely(log: RemoteErrorLog): Promise<string | null> {
  try {
    systemLogger.debug('Logging error remotely', { errorType: log.errorType, operation: log.operationName });

    // Get session info
    const { data: { session } } = await supabase.auth.getSession();

    const browserInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled
    };

    // Call edge function to log error (with short timeout to not block user)
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000); // 3 second timeout

    try {
      const response = await supabase.functions.invoke('log-error', {
        body: {
          ...log,
          sessionValid: !!session,
          jwtExpiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
          browserInfo: { ...browserInfo, ...log.browserInfo }
        }
      });

      clearTimeout(timeout);

      if (response.data?.errorId) {
        systemLogger.debug('Error logged remotely', { errorId: response.data.errorId });
        return response.data.errorId as string;
      }

      if (response.error) {
        systemLogger.warn('Failed to log error remotely', response.error);
      }

      return null;

    } catch (invokeError) {
      clearTimeout(timeout);

      // Silent fail - don't block user flow if error logging fails
      systemLogger.warn('Exception while logging error remotely', invokeError);
      return null;
    }

  } catch (error) {
    // Silent fail - don't block user flow if error logging fails
    systemLogger.warn('Failed to log error remotely', error);
    return null;
  }
}
